import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import { listProducts, listSizeTemplates, type ProductRecord, type SizeTemplateRecord } from '../../services/catalog';
import {
  deleteOrderTypeDiscount,
  listOrderTypeDiscounts,
  setOrderTypeDiscountActive,
  upsertOrderTypeDiscount,
  type OrderType,
  type OrderTypeDiscountType,
  type ProductTypeOption,
  type OrderTypeDiscountRecord,
} from '../../services/orderTypeDiscounts';

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  delivery: 'Teslimat',
  takeaway: 'Gel-al',
  dine_in: 'Restoranda ye',
};

const PRODUCT_TYPE_LABELS: Record<ProductTypeOption, string> = {
  pizza: 'Pizzalar',
  drink: 'İçecekler',
  sauce: 'Soslar',
  snack: 'Atıştırmalıklar',
  combo: 'Menüler',
};

type ScopeOption = 'global' | 'product_type' | 'product';

const discountSchema = z
  .object({
    orderType: z.enum(['delivery', 'takeaway', 'dine_in']),
    scope: z.enum(['global', 'product_type', 'product']),
    productType: z.string().optional(),
    productIds: z.array(z.string()).optional(),
    sizeTemplateIds: z.array(z.string()).optional(),
    discountType: z.enum(['fixed', 'percentage']),
    discountAmount: z.coerce.number().min(0, 'İndirim tutarı 0 veya daha büyük olmalı'),
    startsAt: z.string().optional().or(z.literal('')),
    endsAt: z.string().optional().or(z.literal('')),
    isActive: z.boolean().default(true),
  })
  .superRefine((values, ctx) => {
    if (values.scope === 'product_type' && !values.productType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['productType'],
        message: 'Lütfen bir ürün tipi seçin',
      });
    }
    if (values.scope === 'product') {
      if (!values.productIds || values.productIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['productIds'],
          message: 'En az bir ürün seçin',
        });
      }
    }
    if (values.scope === 'product_type' && values.productType === 'pizza') {
      if (!values.sizeTemplateIds || values.sizeTemplateIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sizeTemplateIds'],
          message: 'Bir veya daha fazla pizza boyutu seçin',
        });
      }
    }
    if (values.discountType === 'percentage' && values.discountAmount > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountAmount'],
        message: 'Yüzde indirim 100 değerinden büyük olamaz',
      });
    }
  });

type DiscountFormValues = z.infer<typeof discountSchema>;

const DEFAULT_VALUES: DiscountFormValues = {
  orderType: 'takeaway',
  scope: 'global',
  productType: '',
  productIds: [],
  sizeTemplateIds: [],
  discountType: 'fixed',
  discountAmount: 0,
  startsAt: '',
  endsAt: '',
  isActive: true,
};

function describeScope(
  discount: OrderTypeDiscountRecord,
  sizeTemplateMap: Record<string, string>,
  productMap: Record<string, string>,
) {
  let label = 'Tüm ürünler';
  if (discount.productIds.length > 0) {
    const names = discount.productIds.map((productId) => productMap[productId]).filter(Boolean);
    if (names.length === 1) {
      label = names[0];
    } else if (names.length > 1) {
      const visibleNames = names.slice(0, 3).join(', ');
      label = names.length > 3 ? `${visibleNames} +${names.length - 3} ürün` : visibleNames;
    } else {
      label = discount.productName ?? 'Belirli ürünler';
    }
  } else if (discount.productType) {
    label = PRODUCT_TYPE_LABELS[discount.productType] ?? discount.productType;
  }
  if (discount.sizeTemplateIds?.length) {
    const sizes = discount.sizeTemplateIds
      .map((id) => sizeTemplateMap[id] ?? 'Boyut')
      .join(', ');
    return `${label} · ${sizes}`;
  }
  return label;
}

function formatSchedule(startsAt: string | null, endsAt: string | null) {
  if (!startsAt && !endsAt) return 'Süresiz';
  const options = { timeZone: 'Europe/Istanbul' } as const;
  const start = startsAt ? new Date(startsAt).toLocaleDateString('tr-TR', options) : 'Hemen';
  const end = endsAt ? new Date(endsAt).toLocaleDateString('tr-TR', options) : 'Süresiz';
  return `${start} → ${end}`;
}

function formatDiscountValue(discountType: OrderTypeDiscountType, value: number) {
  if (discountType === 'percentage') {
    return `%${value.toFixed(value % 1 === 0 ? 0 : 2)}`;
  }
  return `₺${value.toFixed(2)}`;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export default function OrderTypeDiscountManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<OrderTypeDiscountRecord | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<'success' | 'error'>('success');

  const { data: discounts = [], isLoading: discountsLoading } = useQuery({
    queryKey: ['marketing', 'order-type-discounts'],
    queryFn: listOrderTypeDiscounts,
  });

  const { data: products = [] } = useQuery<ProductRecord[]>({
    queryKey: ['catalog', 'all-products'],
    queryFn: listProducts,
  });

  const { data: sizeTemplates = [] } = useQuery<SizeTemplateRecord[]>({
    queryKey: ['catalog', 'size-templates'],
    queryFn: listSizeTemplates,
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<DiscountFormValues>({
    resolver: zodResolver(discountSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const scope = watch('scope');
  const selectedProductType = watch('productType');
  const selectedProductIds = watch('productIds') ?? [];
  const selectedSizeTemplateIds = watch('sizeTemplateIds') ?? [];
  const selectedDiscountType = watch('discountType');

  useEffect(() => {
    if (scope !== 'product') {
      setValue('productIds', [], { shouldValidate: true });
    }
  }, [scope, setValue]);

  useEffect(() => {
    if (scope !== 'product_type') {
      setValue('productType', '', { shouldValidate: true });
      setValue('sizeTemplateIds', [], { shouldValidate: true });
      return;
    }
    if (selectedProductType !== 'pizza') {
      setValue('sizeTemplateIds', [], { shouldValidate: true });
    }
  }, [scope, selectedProductType, setValue]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingDiscount(null);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const openCreate = () => {
    setEditingDiscount(null);
    setStatusMessage(null);
    reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (discount: OrderTypeDiscountRecord) => {
    setStatusMessage(null);
    const scope: ScopeOption = discount.productIds.length > 0 || discount.productId
      ? 'product'
      : discount.productType
        ? 'product_type'
        : 'global';

    setEditingDiscount(discount);
    reset({
      orderType: discount.orderType,
      scope,
      productType: discount.productType ?? '',
      productIds: discount.productIds.length > 0
        ? discount.productIds
        : discount.productId
          ? [discount.productId]
          : [],
      sizeTemplateIds: discount.sizeTemplateIds ?? [],
      discountType: discount.discountType,
      discountAmount: discount.discountAmount,
      startsAt: discount.startsAt?.slice(0, 10) ?? '',
      endsAt: discount.endsAt?.slice(0, 10) ?? '',
      isActive: discount.isActive,
    });
    setModalOpen(true);
  };

  const upsertMutation = useMutation({
    mutationFn: upsertOrderTypeDiscount,
    onSuccess: () => {
      setStatusTone('success');
      setStatusMessage(editingDiscount ? 'İndirim kuralı güncellendi.' : 'İndirim kuralı kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'order-type-discounts'] });
      closeModal();
    },
    onError: (error) => {
      console.error('Failed to save order type discount', error);
      setStatusTone('error');
      setStatusMessage(getErrorMessage(error, 'İndirim kuralı kaydedilemedi. Lütfen tekrar deneyin.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (discount: OrderTypeDiscountRecord) => deleteOrderTypeDiscount(discount.id),
    onSuccess: () => {
      setStatusTone('success');
      setStatusMessage('İndirim kuralı silindi.');
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'order-type-discounts'] });
    },
    onError: (error) => {
      console.error('Failed to delete order type discount', error);
      setStatusTone('error');
      setStatusMessage(getErrorMessage(error, 'İndirim kuralı silinemedi. Lütfen tekrar deneyin.'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (discount: OrderTypeDiscountRecord) =>
      setOrderTypeDiscountActive(discount.id, !discount.isActive),
    onSuccess: () => {
      setStatusTone('success');
      setStatusMessage('İndirim kuralının durumu güncellendi.');
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'order-type-discounts'] });
    },
    onError: (error) => {
      console.error('Failed to toggle order type discount', error);
      setStatusTone('error');
      setStatusMessage(getErrorMessage(error, 'İndirim kuralı güncellenemedi. Lütfen tekrar deneyin.'));
    },
  });

  const availableProducts = useMemo(() => {
    return products.filter((product) => product.isAvailable || selectedProductIds.includes(product.id));
  }, [products, selectedProductIds]);

  const productMap = useMemo(() => {
    const map: Record<string, string> = {};
    products.forEach((product) => {
      map[product.id] = product.name;
    });
    return map;
  }, [products]);

  const sizeTemplateMap = useMemo(() => {
    const map: Record<string, string> = {};
    sizeTemplates.forEach((template) => {
      map[template.id] = template.name ?? template.slug;
    });
    return map;
  }, [sizeTemplates]);

  const onSubmit = (values: DiscountFormValues) => {
    setStatusMessage(null);

    const basePayload = {
      id: editingDiscount?.id,
      orderType: values.orderType,
      productType: values.scope === 'product_type' ? (values.productType as ProductTypeOption) ?? null : null,
      sizeTemplateIds:
        values.scope === 'product_type' && values.productType === 'pizza'
          ? values.sizeTemplateIds ?? []
          : null,
      discountType: values.discountType,
      discountAmount: Number(values.discountAmount),
      startsAt: values.startsAt ? new Date(values.startsAt).toISOString() : null,
      endsAt: values.endsAt ? new Date(values.endsAt).toISOString() : null,
      isActive: values.isActive,
    };

    return upsertMutation.mutateAsync({
      ...basePayload,
      productIds: values.scope === 'product' ? values.productIds ?? [] : null,
    });
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Tanımlı indirimler</p>
            <p className="card-subtitle">
              {discountsLoading ? 'İndirim verileri yükleniyor…' : `${discounts.length} aktif kural`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + İndirim Ekle
          </button>
        </div>

        {statusMessage ? <p className={`status-banner ${statusTone}`}>{statusMessage}</p> : null}

        {discounts.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz özel indirim yok</strong>
            <p>Gel-al veya restoranda ye siparişleri için özel bir fiyatlandırma ekleyin.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sipariş tipi</th>
                  <th>Kapsam</th>
                  <th>İndirim</th>
                  <th>Geçerlilik</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((discount) => (
                  <tr key={discount.id}>
                    <td>{ORDER_TYPE_LABELS[discount.orderType]}</td>
                    <td>{describeScope(discount, sizeTemplateMap, productMap)}</td>
                    <td>
                      <strong>{formatDiscountValue(discount.discountType, discount.discountAmount)}</strong>
                    </td>
                    <td>
                      <span className="helper-text">{formatSchedule(discount.startsAt, discount.endsAt)}</span>
                    </td>
                    <td>
                      <span className={`chip ${discount.isActive ? 'success' : 'warning'}`}>
                        {discount.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="button secondary" onClick={() => openEdit(discount)}>
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button ghost"
                          onClick={() => {
                            setStatusMessage(null);
                            toggleMutation.mutate(discount);
                          }}
                          disabled={toggleMutation.isPending}
                        >
                          {discount.isActive ? 'Durdur' : 'Aktifleştir'}
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (window.confirm('Bu indirim kuralını silmek istediğinize emin misiniz?')) {
                              setStatusMessage(null);
                              deleteMutation.mutate(discount);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingDiscount ? 'İndirim kuralını güncelle' : 'Yeni indirim kuralı'}
        wide
      >
        <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-row">
            <label className="form-field">
              <span>Sipariş tipi</span>
              <select {...register('orderType')}>
                {Object.entries(ORDER_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-field">
              <span>Kapsam</span>
              <select {...register('scope')}>
                <option value="global">Tüm ürünler</option>
                <option value="product_type">Kategori / ürün tipi</option>
                <option value="product">Seçili ürün listesi</option>
              </select>
            </label>
          </div>

          {scope === 'product_type' && (
            <label className="form-field">
              <span>Ürün tipi</span>
              <select {...register('productType')}>
                <option value="">Seçin</option>
                {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.productType && <p className="error-text">{errors.productType.message}</p>}
            </label>
          )}

          {scope === 'product' && (
            <label className="form-field">
              <span>Ürünler</span>
              <select
                multiple
                size={6}
                value={selectedProductIds}
                {...register('productIds', {
                  onChange: (event) => {
                    const selected = Array.from(
                      (event.target as HTMLSelectElement).selectedOptions,
                    ).map((option) => option.value);
                    setValue('productIds', selected, { shouldValidate: true });
                  },
                })}
              >
                {availableProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              {errors.productIds && (
                <p className="error-text">{errors.productIds.message as string}</p>
              )}
              {selectedProductIds.length > 0 && (
                <p className="helper-text">{selectedProductIds.length} ürün seçildi</p>
              )}
            </label>
          )}

          {scope === 'product_type' && selectedProductType === 'pizza' && (
            <label className="form-field">
              <span>Pizza boyutları</span>
              <select
                multiple
                size={5}
                value={selectedSizeTemplateIds}
                {...register('sizeTemplateIds', {
                  onChange: (event) => {
                    const selected = Array.from(
                      (event.target as HTMLSelectElement).selectedOptions,
                    ).map((option) => option.value);
                    setValue('sizeTemplateIds', selected, { shouldValidate: true });
                  },
                })}
              >
                {sizeTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name ?? template.slug}
                  </option>
                ))}
              </select>
              {errors.sizeTemplateIds && (
                <p className="error-text">{errors.sizeTemplateIds.message as string}</p>
              )}
            </label>
          )}

          <div className="form-row">
            <label className="form-field">
              <span>İndirim tipi</span>
              <select {...register('discountType')}>
                <option value="fixed">Sabit tutar</option>
                <option value="percentage">Yüzde</option>
              </select>
            </label>

            <label className="form-field">
              <span>{selectedDiscountType === 'percentage' ? 'İndirim yüzdesi (%)' : 'İndirim tutarı (₺)'}</span>
              <input
                type="number"
                step={selectedDiscountType === 'percentage' ? '0.1' : '0.5'}
                min="0"
                max={selectedDiscountType === 'percentage' ? '100' : undefined}
                {...register('discountAmount')}
              />
              {errors.discountAmount && <p className="error-text">{errors.discountAmount.message}</p>}
            </label>

            <label className="form-field">
              <span>Başlangıç</span>
              <input type="date" {...register('startsAt')} />
            </label>

            <label className="form-field">
              <span>Bitiş</span>
              <input type="date" {...register('endsAt')} />
            </label>
          </div>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...register('isActive')} />
            <span>Hemen aktif olsun</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button type="submit" className="button primary" disabled={isSubmitting || upsertMutation.isPending}>
              {editingDiscount ? 'Güncelle' : 'İndirim ekle'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
